/**
 * Hesap türü durumu ve müşteri tarafı kısıtları.
 *
 * Tür satırın değil HESABIN özelliğidir ve tek doğru kaynağı veritabanıdır
 * (profiles.account_type). Buradaki localStorage yalnızca KAYIT anında hangi
 * türün açılacağını taşır; giriş yapıldığı anda DB değeri onu ezer.
 *
 * Buradaki hiçbir kısıt güvenlik değildir. "Müşteri hesabı tek kullanıcı"
 * kuralı veritabanında zorlanır (trg_profiles_account_type tetikleyicisi +
 * profiles_customer_single index'i); bu dosya yalnızca çalışmayacak bir
 * düğmeyi göstermemek için var.
 */

import { legacy } from '../../shell/bridge.ts';
import { MANAGER_NAMES } from './texts.ts';

export type AccountType = 'firm' | 'customer';

/** Kullanıcı sınırı tek yerde. Müşteri tarafı bugün tek kullanıcı; ileride
 *  açılmak istenirse değişecek tek şey bu sayıdır. */
export const AT_MAX_USERS: Record<AccountType, number> = { firm: Infinity, customer: 1 };

/** Müşteri tarafında gizlenen menüler — muhasebe ofisine ait işlevler.
 *  ni8: Takımınla Dosya Paylaş · ni9: Müşterilerim
 *  Sayfalar SİLİNMEZ, yalnızca müşteri menüsünde görünmez. */
export const HIDDEN_NAV_FOR_CUSTOMER = ['ni8', 'ni9'];

const CHOICE_KEY = 'tekoffice.signupRole';

/** Giriş sonrası DB'den gelen gerçek tür. null = henüz bilinmiyor. */
let accountType: AccountType | null = null;

/* ── Seçimin taşınması ─────────────────────────────────────────── */

export function getChoice(): AccountType | null {
  try {
    const v = localStorage.getItem(CHOICE_KEY);
    return v === 'customer' || v === 'firm' ? v : null;
  } catch { return null; }
}

export function setChoice(t: AccountType | null): void {
  try {
    if (t) localStorage.setItem(CHOICE_KEY, t);
    else localStorage.removeItem(CHOICE_KEY);
  } catch { /* özel pencere / kapalı depolama — sessiz geç */ }
}

/** Giriş yapılmışsa hesabın gerçek türü, yapılmamışsa kayıt için seçilen tür. */
export function current(): AccountType {
  return accountType ?? getChoice() ?? 'firm';
}

export function isCustomer(): boolean {
  return current() === 'customer';
}

/**
 * DB'den okunan tür — tek doğru kaynak. sbAfterLogin buradan besler.
 *
 * Kullanıcı giriş öncesi YANLIŞ tarafı seçtiyse (firma hesabıyla
 * "Müşteriyim" demek gibi) DB kazanır — ama sessizce değil. Kurumsal ve
 * müşteri tarafı ayrı hesaplardır; kullanıcı neden beklediği ekranı
 * görmediğini bilmeli, yoksa hatayı bizde arar.
 *
 * Veritabanı bunu ayrıca zorluyor: aynı sahip iki türe sahip olamaz
 * (profiles_account_type_guard). Buradaki uyarı yalnızca görünürlük içindir.
 */
export function setFromDb(t: unknown): AccountType {
  const real: AccountType = (t === 'customer' || t === 'firm') ? t : 'firm';
  const chosen = getChoice();
  accountType = real;
  setChoice(real);
  applyCustomerUi();

  if (chosen && chosen !== real) {
    try {
      legacy.toast(legacy.t(real === 'firm' ? 'at_is_firm' : 'at_is_customer'));
    } catch { /* toast yoksa sessiz geç */ }
  }
  return real;
}

export function clear(): void {
  accountType = null;
}

export function managerName(type: unknown): string {
  if (type === 'customer') return legacy.t('my_account');
  return MANAGER_NAMES.en;
}

/* ── Müşteri tarafı arayüz kısıtları ───────────────────────────── */

function el(id: string): HTMLElement | null {
  return document.getElementById(id);
}

/**
 * Hesap türüne göre arayüzü ayarla.
 *
 * Tür HENÜZ BİLİNMİYORSA hiçbir şeye dokunulmaz: giriş öncesi localStorage
 * seçimi yalnızca kayıt içindir, yetki belirlemez. Aksi halde rol ekranında
 * "Müşteriyim"e bakan biri, sonra firma hesabıyla girse bile kısıtlanmış
 * arayüz görürdü.
 *
 * İki yönlüdür. Tek yönlü (yalnızca gizleyen) sürümü, müşteri seçimi bir kez
 * yapıldıktan sonra "Yeni Kullanıcı Ekle" düğmesini firma hesaplarında da
 * kalıcı olarak yok ediyordu.
 */
export function applyCustomerUi(): void {
  if (accountType === null) return;
  const cust = accountType === 'customer';

  /* Bu düğmeyi BAŞKA HİÇBİR YER yönetmiyor — burada iki yönlü olmak zorunda. */
  const add = el('ls-addbtn');
  if (add) add.style.display = cust ? 'none' : '';

  /* ni8 (dosya paylaşımı) ve ni9 (eski müşteri kartları) katalogdaki menüde
     yok: iki tarafta da gizli (Faz 6). Menünün geri kalanını nav-shape çizer.
     Sayfalar silinmedi; yalnızca menüde görünmezler. */
  for (const id of HIDDEN_NAV_FOR_CUSTOMER) {
    const nav = el(id);
    if (nav) nav.style.display = 'none';
  }

  /* Silme düğmelerini core.js'teki _updateDelBtn yetkiye göre çizer;
     burada yalnızca müşteri tarafında üzerine kapatılır. */
  if (!cust) return;
  for (const id of ['ls-delbtn', 'sb-remove-user-btn']) {
    const b = el(id);
    if (b) b.style.display = 'none';
  }
}

/**
 * core.js yetki düğmelerini yeniden çizdiğinde kısıtı tekrar uygula —
 * yoksa _updateDelBtn düğmeleri geri açar.
 */
export function wrapUpdateDelBtn(): void {
  const w = globalThis as unknown as { _updateDelBtn?: () => void };
  const prev = w._updateDelBtn;
  w._updateDelBtn = function () {
    if (typeof prev === 'function') { try { prev(); } catch { /* eski koda güvenme */ } }
    applyCustomerUi();
  };
}
